import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { all_routes } from '../../../Router/all_routes';
import { base_api_url } from '../../../environment';
import axios from 'axios';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import './Settings.css';

// Importation des icônes depuis react-feather pour une interface moderne
import {
    // Monitor,    // Apparence
    // Globe,      // Langue
    CreditCard, // Abonnement
    DollarSign, // Paiement
    // Bell,       // Notifications
    Shield,     // Sécurité
    // Link as LinkIcon, // Intégrations
    // Sliders,    // Avancé
    Calendar,   // Date de renouvellement
    FileText,   // Périodicité / Factures
    Mail,       // Courriel
    MapPin,     // Adresse
    Eye,        // Voir
    EyeOff,     // Masquer
    Edit2,      // Éditer
    Save,       // Sauvegarder
    X,          // Annuler
    Key,        // Clé
    Headphones,
    MessageCircle,
    Phone,
    BookOpen,
    ChevronDown,
    ChevronUp,
    Send,
    ExternalLink,
    Video,
    MessageSquare,
    Clock,
    CheckCircle,
    Smartphone,
    Bell
} from 'react-feather';

/**
 * Composant de gestion des paramètres de l'établissement.
 * Permet de gérer l'abonnement, la facturation et les préférences générales.
 */
const Settings = () => {
    const MySwal = withReactContent(Swal);
    const navigate = useNavigate();

    // États locaux
    const [activeTab, setActiveTab] = useState('subscription');
    const [subscriptionData, setSubscriptionData] = useState(null);
    const [subscriptionsList, setSubscriptionsList] = useState([]);
    const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);
    const [utilisateur] = useState(JSON.parse(localStorage.getItem('user')) || null);

    // États pour les notifications
    const [notifSettings, setNotifSettings] = useState({
        stockLow: true,
        outOfStock: true,
        trialEnd: true,
        paymentIssue: true,
        newOrders: true,
        emailEnabled: true,
        emailFrequency: 'realtime'
    });

    const handleNotifToggle = (setting) => {
        setNotifSettings(prev => ({
            ...prev,
            [setting]: !prev[setting]
        }));
    };

    const handleFrequencyChange = (e) => {
        setNotifSettings(prev => ({
            ...prev,
            emailFrequency: e.target.value
        }));
    };

    // États pour le support client
    const [faqOpenIndex, setFaqOpenIndex] = useState(null);
    const [ticketData, setTicketData] = useState({ sujet: '', categorie: '', message: '' });

    const toggleFaq = (index) => {
        if (faqOpenIndex === index) {
            setFaqOpenIndex(null);
        } else {
            setFaqOpenIndex(index);
        }
    };

    const handleTicketSubmit = (e) => {
        e.preventDefault();
        // Simulation d'envoi de ticket
        MySwal.fire({
            icon: "success",
            text: "Votre ticket a bien été envoyé. Notre équipe vous répondra dans les plus brefs délais.",
            toast: true,
            position: "bottom-end",
            showConfirmButton: false,
            timer: 4000,
            customClass: { popup: "pixel-toast" }
        });
        setTicketData({ sujet: '', categorie: '', message: '' });
    };

    const faqs = [
        { question: "Comment créer un nouveau restaurant ?", answer: "Pour créer un nouveau restaurant, allez dans la section \"Mes restaurants\" depuis votre profil et cliquez sur le bouton \"Ajouter un restaurant\". Remplissez ensuite les informations requises." },
        { question: "Comment générer un QR code pour mes tables ?", answer: "Allez dans l'onglet \"Tables\", sélectionnez les tables pour lesquelles vous souhaitez générer un QR code, puis cliquez sur le bouton \"Générer QR\" situé en haut de la page." },
        { question: "Comment changer la devise de mon restaurant ?", answer: "Dans les paramètres de votre restaurant (Paramètres généraux), vous trouverez une option déroulante pour sélectionner la devise principale de votre établissement." },
        { question: "Comment inviter un membre de mon équipe ?", answer: "Rendez-vous dans la section \"Équipes\", puis cliquez sur \"Inviter un membre\". Entrez son adresse email et définissez son rôle avant de valider l'invitation." },
        { question: "Comment suivre mes dépenses ?", answer: "Le suivi des dépenses s'effectue dans la rubrique \"Comptabilité\" > \"Dépenses\". Vous pourrez y ajouter, catégoriser et visualiser l'historique de vos sorties d'argent." },
    ];

    const [formData, setFormData] = useState({
        "nom_facturation": "",
        "adresse_facturation": "",
        "email_facturation": "",
        "num_siren": "",
        "num_tva": "",
    });

    // État pour les informations de paiement Stripe
    const [paymentData, setPaymentData] = useState({
        "pk_stripe": "",
        "sk_stripe": "",
    });
    const [isEditingPayment, setIsEditingPayment] = useState(false);
    const [showSecretKey, setShowSecretKey] = useState(false);

    // Fonction pour récupérer l'historique des abonnements
    const fetchSubscriptionHistory = useCallback(async (userId) => {
        setLoadingSubscriptions(true);
        const token = utilisateur?.access_token;

        try {
            const url = `${base_api_url}/abonnement/abonnement/${userId}`;
            const response = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.status === 200 || response.status === 201) {
                const data = response.data;
                console.log('Abonnements récupérés:', data);

                let abonnementsList = [];

                if (Array.isArray(data)) {
                    abonnementsList = data;
                } else if (data && typeof data === 'object') {
                    // Si c'est un objet avec une clé 'abonnements' qui est un tableau
                    if (data.abonnements && Array.isArray(data.abonnements)) {
                        abonnementsList = data.abonnements;
                    }
                    // Si c'est un seul objet d'abonnement (cas d'un retour unique)
                    else if (data.id_abonnement) {
                        abonnementsList = [data];
                    }
                }

                setSubscriptionsList(abonnementsList);

                // 🎯 Trouver l'abonnement actif le plus récent
                const now = new Date();
                const activeSubscription = abonnementsList
                    .filter(sub => {
                        const dateDebut = new Date(sub.date_debut);
                        const dateFin = new Date(sub.date_fin);
                        return dateDebut <= now && dateFin >= now;
                    })
                    .sort((a, b) => new Date(b.date_debut) - new Date(a.date_debut))[0];

                if (activeSubscription) {
                    console.log('✅ Abonnement actif trouvé:', activeSubscription);
                    setSubscriptionData(activeSubscription);
                } else {
                    console.warn('⚠️ Aucun abonnement actif trouvé');
                    setSubscriptionData(null);
                }
            }
        } catch (error) {
            console.error('Erreur lors du chargement de l\'historique des abonnements:', error);

            // Afficher un message d'erreur uniquement si ce n'est pas une erreur 404 (pas d'abonnements)
            if (error.response && error.response.status !== 404) {
                Swal.fire({
                    icon: 'error',
                    title: 'Erreur',
                    text: 'Impossible de charger l\'historique des abonnements',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true,
                    customClass: {
                        popup: "pixel-toast",
                    },
                });
            }

            setSubscriptionsList([]);
            setSubscriptionData(null); // Pas d'abonnement actif
        } finally {
            setLoadingSubscriptions(false);
        }
    }, [utilisateur]); // ✅ Retiré MySwal des dépendances pour éviter la boucle infinie

    // Charger l'historique des abonnements au démarrage
    useEffect(() => {
        if (utilisateur && utilisateur.id_utilisateur) {
            console.log('🔄 Chargement de l\'historique pour utilisateur:', utilisateur.id_utilisateur);
            fetchSubscriptionHistory(utilisateur.id_utilisateur);
        }
    }, [utilisateur, fetchSubscriptionHistory]);

    // Charger les infos DE FACTURATION EXISTANTES 
    useEffect(() => {
        const fetchData = async () => {
            const token = utilisateur?.access_token;

            try {
                const response = await axios.get(`${base_api_url}api/users_connect`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                const InfosFacturation = response.data;
                setFormData({
                    nom_facturation: InfosFacturation?.nom_facturation || "",
                    email_facturation: InfosFacturation?.email_facturation || "",
                    num_siren: InfosFacturation?.num_siren || "",
                    num_tva: InfosFacturation?.num_tva || "",
                    adresse_facturation: InfosFacturation?.adresse_facturation || "",
                });

                // Charger les informations de paiement Stripe
                setPaymentData({
                    pk_stripe: InfosFacturation?.pk_stripe || "",
                    sk_stripe: InfosFacturation?.sk_stripe || "",
                });
            } catch (error) {
                console.error("Erreur de récupération :", error);
            }
        };
        fetchData();
    }, [utilisateur]);

    /*  useEffect(() => {
         if (utilisateur) {
             console.log("Infos récupérées :", utilisateur);
             setFormData({
                 nom_facturation: utilisateur?.nom_facturation || "",
                 email_facturation: utilisateur?.email_facturation || "",
                 num_siren: utilisateur?.num_siren || "",
                 num_tva: utilisateur?.num_tva || "",
                 adresse_facturation: utilisateur?.adresse_facturation || "",
             });
         }
     }, []);
  */

    // en cas de changement fde valeur d'un element du formulaire sera setFormData lancer pour mettre a jour les donne
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    // pour la soumission du formulaire
    const handleSubmit = async (e) => {
        e.preventDefault(); // éviter le rechargement de la page
        const token = utilisateur?.access_token;

        try {
            // Mettre à jour les infos
            const responsePut = await axios.put(`${base_api_url}api/user`, formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            console.log("Données mises à jour :", responsePut.data);

            MySwal.fire({
                icon: "success",
                // title: "Inscription reussite !",
                text: "vos informations ont été enregistrées avec succès !",
                toast: true,
                position: "bottom-end",
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true,
                customClass: {
                    popup: "pixel-toast",
                },
            });
        } catch (error) {
            MySwal.fire({
                icon: "error",
                // title: "Avertissement !",
                text: "Erreur lors de la modification des informations.",
                toast: true,
                position: "bottom-end",
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true,
                customClass: {
                    popup: "pixel-toast",
                },
            });
        }
    };

    // Fonctions pour gérer les informations de paiement Stripe
    const handlePaymentChange = (e) => {
        const { name, value } = e.target;
        setPaymentData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const handleEditPayment = (e) => {
        e.preventDefault();
        setIsEditingPayment(true);
    };

    const handleCancelPaymentEdit = (e) => {
        e.preventDefault();
        // Recharger les données originales depuis localStorage ou API
        const userFromStorage = JSON.parse(localStorage.getItem('user')) || null;
        setPaymentData({
            pk_stripe: userFromStorage?.pk_stripe || "",
            sk_stripe: userFromStorage?.sk_stripe || "",
        });
        setIsEditingPayment(false);
        setShowSecretKey(false);
    };

    const handleSubmitPayment = async (e) => {
        e.preventDefault();
        const token = utilisateur?.access_token;

        try {
            const responsePut = await axios.put(`${base_api_url}api/user`, paymentData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            console.log("Clés Stripe mises à jour :", responsePut.data);

            // Mettre à jour le localStorage
            const updatedUser = { ...utilisateur, ...paymentData };
            localStorage.setItem('user', JSON.stringify(updatedUser));

            MySwal.fire({
                icon: "success",
                text: "Vos clés Stripe ont été enregistrées avec succès !",
                toast: true,
                position: "bottom-end",
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true,
                customClass: {
                    popup: "pixel-toast",
                },
            });

            setIsEditingPayment(false);
            setShowSecretKey(false);
        } catch (error) {
            console.error("Erreur lors de la mise à jour des clés Stripe:", error);
            MySwal.fire({
                icon: "error",
                text: "Erreur lors de la modification des clés Stripe.",
                toast: true,
                position: "bottom-end",
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true,
                customClass: {
                    popup: "pixel-toast",
                },
            });
        }
    };

    // Configuration des onglets de la barre latérale des paramètres
    const tabs = [
        /*  { id: 'appearance', label: 'Apparence', icon: <Monitor /> },
         { id: 'language', label: 'Langue et localisation', icon: <Globe /> }, */
        { id: 'subscription', label: 'Abonnement et facturation', icon: <CreditCard /> },
        { id: 'payment', label: 'Moyens de paiement', icon: <DollarSign /> },
        { id: 'support', label: 'Support client', icon: <Headphones /> },
        { id: 'notifications', label: 'Notifications', icon: <Bell /> },
        /*    { id: 'security', label: 'Sécurité et accès', icon: <Shield /> },
          { id: 'integrations', label: 'Intégrations', icon: <LinkIcon /> },
          { id: 'advanced', label: 'Avancé', icon: <Sliders /> }, */
    ];

    // Fonction pour formater les dates
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    // Fonction pour obtenir le nom du plan selon le type
    const getPlanName = (type) => {
        switch (type) {
            case 1: return 'Starter';
            case 2: return 'Planificateur';
            case 3: return 'Premium';
            default: return 'Inconnu';
        }
    };

    // Fonction pour obtenir la périodicité
    const getPeriodicity = () => {
        return 'Mensuel'; // A AJUSTER
    };

    // Fonction pour obtenir le statut de l'abonnement basé sur la date de fin
    const getSubscriptionStatus = (subscription) => {
        if (!subscription || !subscription.date_fin) return 'Inconnu';

        try {
            const today = new Date();
            today.setHours(0, 0, 0, 0); // On compare uniquement les dates, pas les heures

            // Gérer les formats de date possibles (YYYY/MM/DD ou YYYY-MM-DD)
            const dateFinStr = subscription.date_fin.replace(/\//g, '-');
            const dateFin = new Date(dateFinStr);
            dateFin.setHours(0, 0, 0, 0);

            if (dateFin >= today) {
                return 'Actif';
            } else {
                return 'Expiré';
            }
        } catch (error) {
            console.error("Erreur lors du calcul du statut:", error);
            return 'Inconnu';
        }
    };

    return (
        <div className="page-wrapper">
            <div className="content">
                <div className="settings-page">

                {/* EN-TÊTE : Titre et description de la page */}
                <div className="settings-header">
                    <h2>Paramètres</h2>
                    <p>Configuration générale, abonnements et préférences</p>
                </div>

                {/* BARRE D'ONGLETS : Navigation entre les différentes sections */}
                <div className="settings-tabs">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            className={`settings-tab ${activeTab === tab.id ? 'active' : ''}`}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            {tab.icon}
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* CONTENU DE L'ONGLET : Affichage dynamique selon l'onglet sélectionné */}
                <div className="settings-content">

                    {/* SECTION : ABONNEMENT ET FACTURATION */}
                    {activeTab === 'subscription' && (
                        <div className="subscription-tab-content">

                            {/* Carte : Abonnement Actuel */}
                            <div className="settings-card featured">
                                <div className="card-title">
                                    <CreditCard size={18} />
                                    <span>Abonnement actuel</span>
                                </div>

                                {subscriptionData && subscriptionData.id_abonnement ? (
                                    <>
                                        <div className="plan-info-header">
                                            <div className="plan-info-container">
                                                <div className="plan-details-left">
                                                    <div className="plan-title">{getPlanName(subscriptionData.type_abonnement)}</div>
                                                    <div className="plan-price">{subscriptionData.montant}€ / mois</div>
                                                </div>
                                                <div className={`status-badge ${getSubscriptionStatus(subscriptionData) === 'Actif' ? 'active' : 'expired'}`}>
                                                    {getSubscriptionStatus(subscriptionData)}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Grille de détails : Renouvellement et Périodicité */}
                                        <div className="details-grid">
                                            <div className="detail-item">
                                                <div className="detail-icon">
                                                    <Calendar size={18} />
                                                </div>
                                                <div className="detail-text">
                                                    <span className="detail-label">Date de début</span>
                                                    <span className="detail-value">{formatDate(subscriptionData.date_debut)}</span>
                                                </div>
                                            </div>
                                            <div className="detail-item">
                                                <div className="detail-icon">
                                                    <Calendar size={18} />
                                                </div>
                                                <div className="detail-text">
                                                    <span className="detail-label">Renouvellement</span>
                                                    <span className="detail-value">{formatDate(subscriptionData.date_fin)}</span>
                                                </div>
                                            </div>
                                            <div className="detail-item">
                                                <div className="detail-icon">
                                                    <FileText size={18} />
                                                </div>
                                                <div className="detail-text">
                                                    <span className="detail-label">Périodicité</span>
                                                    <span className="detail-value">{getPeriodicity()}</span>
                                                </div>
                                            </div>
                                            <div className="detail-item">
                                                <div className="detail-icon">
                                                    <CreditCard size={18} />
                                                </div>
                                                <div className="detail-text">
                                                    <span className="detail-label">Mode de paiement</span>
                                                    <span className="detail-value" style={{ textTransform: 'capitalize' }}>
                                                        {subscriptionData.mode_paiement || 'N/A'}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="detail-item">
                                                <div className="detail-icon">
                                                    <Calendar size={18} />
                                                </div>
                                                <div className="detail-text">
                                                    <span className="detail-label">Date de paiement</span>
                                                    <span className="detail-value">{formatDate(subscriptionData.date_paiement)}</span>
                                                </div>
                                            </div>
                                            <div className="detail-item">
                                                <div className="detail-icon">
                                                    <FileText size={18} />
                                                </div>
                                                <div className="detail-text">
                                                    <span className="detail-label">ID Abonnement</span>
                                                    <span className="detail-value">{subscriptionData.id_abonnement}</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Référence de paiement */}
                                        {subscriptionData.reference_paiement && (
                                            <div style={{
                                                padding: '12px 16px',
                                                backgroundColor: '#f9fafb',
                                                borderRadius: '8px',
                                                marginBottom: '20px',
                                                border: '1px solid #f3f4f6'
                                            }}>
                                                <div style={{
                                                    fontSize: '11px',
                                                    fontWeight: 700,
                                                    color: '#111827',
                                                    marginBottom: '4px'
                                                }}>
                                                    Référence de paiement
                                                </div>
                                                <div style={{
                                                    fontSize: '12px',
                                                    color: '#6b7280',
                                                    fontFamily: 'monospace',
                                                    wordBreak: 'break-all'
                                                }}>
                                                    {subscriptionData.reference_paiement}
                                                </div>
                                            </div>
                                        )}

                                        {/* Boutons d'action pour l'abonnement */}
                                        <div className="settings-buttons">
                                            <button
                                                className="btn-primary-settings"
                                                onClick={() => navigate(all_routes.pricing)}
                                            >
                                                Mettre à niveau mon abonnement
                                            </button>
                                            {/* <button
                                                className="btn-secondary-settings"
                                                onClick={() => navigate(all_routes.pricing)}
                                            >
                                                Changer de plan
                                            </button> */}
                                        </div>
                                    </>
                                ) : (
                                    <div style={{
                                        padding: '48px 32px',
                                        textAlign: 'center',
                                        backgroundColor: '#fef9f6'
                                    }}>
                                        <div style={{
                                            width: '80px',
                                            height: '80px',
                                            margin: '0 auto 24px',
                                            backgroundColor: '#fff',
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            boxShadow: '0 4px 12px rgba(255, 106, 61, 0.1)'
                                        }}>
                                            <CreditCard size={40} style={{
                                                color: '#ff6a3d',
                                                strokeWidth: 1.5
                                            }} />
                                        </div>
                                        <h3 style={{
                                            fontSize: '20px',
                                            fontWeight: 700,
                                            color: '#111827',
                                            marginBottom: '12px'
                                        }}>
                                            Aucun abonnement actif
                                        </h3>
                                        <p style={{
                                            fontSize: '15px',
                                            marginBottom: '32px',
                                            lineHeight: '1.6',
                                            color: '#6b7280',
                                            maxWidth: '500px',
                                            margin: '0 auto 32px'
                                        }}>
                                            Vous n&apos;avez pas encore souscrit à un abonnement. Choisissez le plan qui correspond le mieux à vos besoins pour débloquer toutes les fonctionnalités de QR Nyama.
                                        </p>
                                        <button
                                            className="btn-primary-settings"
                                            onClick={() => navigate(all_routes.pricing)}
                                            style={{
                                                margin: '0 auto',
                                                padding: '12px 32px',
                                                fontSize: '15px'
                                            }}
                                        >
                                            Découvrir nos offres
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Carte : Informations de Facturation */}
                            <div className="settings-card">
                                <div className="card-title">
                                    <CreditCard size={18} />
                                    <span>Informations de facturation</span>
                                </div>

                                <form className="billing-form" onSubmit={handleSubmit}>
                                    <div className="form-group">
                                        <label>Nom du facturateur</label>
                                        <input
                                            type="text"
                                            className="form-input"
                                            name="nom_facturation"
                                            value={formData.nom_facturation}
                                            onChange={handleChange}
                                            placeholder="QR Nyama SARL"
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>
                                            <Mail size={14} />
                                            Courriel de facturation
                                        </label>
                                        <input
                                            type="email"
                                            className="form-input"
                                            name="email_facturation"
                                            value={formData.email_facturation}
                                            onChange={handleChange}
                                            placeholder="facturation@qrnyama.com"
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Numéro SIREN</label>
                                        <input
                                            type="text"
                                            className="form-input"
                                            name="num_siren"
                                            value={formData.num_siren}
                                            onChange={handleChange}
                                            placeholder="123456789"
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Montant TVA</label>
                                        <input
                                            type="text"
                                            className="form-input"
                                            name="num_tva"
                                            value={formData.num_tva}
                                            onChange={handleChange}
                                            placeholder="19%"
                                            required
                                        />
                                    </div>
                                    <div className="form-group full-width">
                                        <label>
                                            <MapPin size={14} />
                                            Adresse de facturation
                                        </label>
                                        <input
                                            type="text"
                                            className="form-input"
                                            name="adresse_facturation"
                                            value={formData.adresse_facturation}
                                            onChange={handleChange}
                                            placeholder="123 Rue de la Restauration, 75001 Paris"
                                            required
                                        />
                                    </div>
                                    <div className="settings-buttons" style={{ marginTop: '24px' }}>
                                        <button className="btn-primary-settings" type="submit">
                                            Enregistrer les modifications
                                        </button>
                                    </div>
                                </form>
                            </div>

                            {/* Section : Historique des Factures */}
                            <div className="invoice-history-section">
                                <h3 className="invoice-history-title">Historique des abonnements</h3>
                                <div className="invoice-table-wrapper">
                                    {loadingSubscriptions ? (
                                        <div style={{ padding: '40px', textAlign: 'center' }}>
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="sr-only">Chargement...</span>
                                            </div>
                                            <p style={{ marginTop: '16px', color: '#6b7280' }}>
                                                Chargement de l&apos;historique...
                                            </p>
                                        </div>
                                    ) : subscriptionsList.length === 0 ? (
                                        <div style={{
                                            padding: '40px',
                                            textAlign: 'center',
                                            color: '#6b7280'
                                        }}>
                                            <FileText size={48} style={{
                                                marginBottom: '16px',
                                                color: '#d1d5db',
                                                strokeWidth: 1.5
                                            }} />
                                            <p style={{ fontSize: '15px', fontWeight: 500 }}>
                                                Aucun historique d&apos;abonnement disponible
                                            </p>
                                        </div>
                                    ) : (
                                        <table className="invoice-table">
                                            <thead>
                                                <tr>
                                                    <th>ID</th>
                                                    <th>Référence</th>
                                                    <th>Plan</th>
                                                    <th>Date de début</th>
                                                    <th>Date de fin</th>
                                                    <th>Montant</th>
                                                    <th>Statut</th>
                                                    <th>Paiement</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {subscriptionsList.map((subscription) => (
                                                    <tr key={subscription.id_abonnement}>
                                                        <td>{subscription.id_abonnement}</td>
                                                        <td style={{ fontSize: '12px', fontFamily: 'monospace' }}>
                                                            {subscription.reference_paiement || 'N/A'}
                                                        </td>
                                                        <td>{getPlanName(subscription.type_abonnement)}</td>
                                                        <td>{formatDate(subscription.date_debut)}</td>
                                                        <td>{formatDate(subscription.date_fin)}</td>
                                                        <td style={{ fontWeight: 600 }}>{subscription.montant}€</td>
                                                        <td>
                                                            <span className={
                                                                getSubscriptionStatus(subscription) === 'Actif'
                                                                    ? 'status-beneficiary'
                                                                    : 'status-expired'
                                                            }>
                                                                {getSubscriptionStatus(subscription)}
                                                            </span>
                                                        </td>
                                                        <td style={{ textTransform: 'capitalize' }}>
                                                            {subscription.mode_paiement || 'N/A'}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* SECTION : MOYENS DE PAIEMENT */}
                    {activeTab === 'payment' && (
                        <div className="payment-tab-content">
                            {/* Carte : Configuration Stripe */}
                            <div className="settings-card featured">
                                <div className="card-title">
                                    <DollarSign size={18} />
                                    <span>Configuration Stripe</span>
                                </div>

                                <div style={{
                                    padding: '16px 20px',
                                    backgroundColor: '#fef3c7',
                                    borderRadius: '8px',
                                    marginBottom: '24px',
                                    border: '1px solid #fbbf24',
                                    display: 'flex',
                                    alignItems: 'start',
                                    gap: '12px'
                                }}>
                                    <Shield size={20} style={{ color: '#d97706', marginTop: '2px', flexShrink: 0 }} />
                                    <div>
                                        <div style={{ fontWeight: 600, color: '#92400e', marginBottom: '4px', fontSize: '14px' }}>
                                            Information importante
                                        </div>
                                        <div style={{ fontSize: '13px', color: '#78350f', lineHeight: '1.5' }}>
                                            Ces clés sont sensibles et permettent de gérer les paiements de votre établissement.
                                            Ne les partagez jamais et conservez-les en sécurité.
                                            Vous pouvez obtenir vos clés depuis votre{' '}
                                            <a
                                                href="https://dashboard.stripe.com/apikeys"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{ color: '#92400e', fontWeight: 600, textDecoration: 'underline' }}
                                            >
                                                tableau de bord Stripe
                                            </a>.
                                        </div>
                                    </div>
                                </div>

                                <form className="billing-form" onSubmit={handleSubmitPayment}>
                                    {/* Clé publique Stripe */}
                                    <div className="form-group full-width">
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <Key size={14} />
                                            Clé publique Stripe (pk_)
                                        </label>
                                        <div style={{ position: 'relative' }}>
                                            <input
                                                type="text"
                                                className="form-input"
                                                name="pk_stripe"
                                                value={paymentData.pk_stripe}
                                                onChange={handlePaymentChange}
                                                placeholder="pk_live_..."
                                                disabled={!isEditingPayment}
                                                style={{
                                                    backgroundColor: isEditingPayment ? '#fff' : '#f9fafb',
                                                    cursor: isEditingPayment ? 'text' : 'not-allowed',
                                                    paddingRight: '40px',
                                                    fontFamily: 'monospace',
                                                    fontSize: '13px'
                                                }}
                                            />
                                            {paymentData.pk_stripe && (
                                                <div style={{
                                                    position: 'absolute',
                                                    right: '12px',
                                                    top: '50%',
                                                    transform: 'translateY(-50%)',
                                                    padding: '4px 8px',
                                                    backgroundColor: '#dcfce7',
                                                    color: '#166534',
                                                    borderRadius: '4px',
                                                    fontSize: '11px',
                                                    fontWeight: 600
                                                }}>
                                                    Configurée
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Clé secrète Stripe */}
                                    <div className="form-group full-width">
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <Shield size={14} />
                                            Clé secrète Stripe (sk_)
                                        </label>
                                        <div style={{ position: 'relative' }}>
                                            <input
                                                type={showSecretKey ? "text" : "password"}
                                                className="form-input"
                                                name="sk_stripe"
                                                value={paymentData.sk_stripe}
                                                onChange={handlePaymentChange}
                                                placeholder="sk_live_..."
                                                disabled={!isEditingPayment}
                                                style={{
                                                    backgroundColor: isEditingPayment ? '#fff' : '#f9fafb',
                                                    cursor: isEditingPayment ? 'text' : 'not-allowed',
                                                    paddingRight: '80px',
                                                    fontFamily: 'monospace',
                                                    fontSize: '13px'
                                                }}
                                            />
                                            <div style={{
                                                position: 'absolute',
                                                right: '12px',
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px'
                                            }}>
                                                {paymentData.sk_stripe && (
                                                    <div style={{
                                                        padding: '4px 8px',
                                                        backgroundColor: '#dcfce7',
                                                        color: '#166534',
                                                        borderRadius: '4px',
                                                        fontSize: '11px',
                                                        fontWeight: 600
                                                    }}>
                                                        Configurée
                                                    </div>
                                                )}
                                                {paymentData.sk_stripe && (
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowSecretKey(!showSecretKey)}
                                                        style={{
                                                            background: 'none',
                                                            border: 'none',
                                                            padding: '4px',
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            color: '#6b7280'
                                                        }}
                                                        title={showSecretKey ? "Masquer" : "Afficher"}
                                                    >
                                                        {showSecretKey ? <EyeOff size={16} /> : <Eye size={16} />}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Boutons d'action */}
                                    <div className="settings-buttons" style={{ marginTop: '24px' }}>
                                        {!isEditingPayment ? (
                                            <button
                                                type="button"
                                                className="btn-primary-settings"
                                                onClick={handleEditPayment}
                                                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                                            >
                                                <Edit2 size={16} />
                                                Modifier les clés
                                            </button>
                                        ) : (
                                            <>
                                                <button
                                                    type="submit"
                                                    className="btn-primary-settings"
                                                    style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                                                >
                                                    <Save size={16} />
                                                    Enregistrer les modifications
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn-secondary-settings"
                                                    onClick={handleCancelPaymentEdit}
                                                    style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                                                >
                                                    <X size={16} />
                                                    Annuler
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </form>
                            </div>

                            {/* Carte : Informations supplémentaires */}
                            {/* <div className="settings-card">
                                <div className="card-title">
                                    <LinkIcon size={18} />
                                    <span>À propos de Stripe</span>
                                </div>

                                <div style={{ color: '#6b7280', fontSize: '14px', lineHeight: '1.7' }}>
                                    <p style={{ marginBottom: '16px' }}>
                                        Stripe est notre solution de paiement sécurisée qui vous permet de recevoir 
                                        des paiements par carte bancaire de vos clients.
                                    </p>
                                    
                                    <div style={{ 
                                        backgroundColor: '#f9fafb', 
                                        padding: '16px', 
                                        borderRadius: '8px',
                                        marginBottom: '16px'
                                    }}>
                                        <div style={{ fontWeight: 600, color: '#111827', marginBottom: '12px' }}>
                                            Fonctionnalités incluses :
                                        </div>
                                        <ul style={{ 
                                            margin: 0, 
                                            paddingLeft: '20px',
                                            listStyleType: 'disc'
                                        }}>
                                            <li style={{ marginBottom: '8px' }}>Paiements sécurisés par carte bancaire</li>
                                            <li style={{ marginBottom: '8px' }}>Suivi des transactions en temps réel</li>
                                            <li style={{ marginBottom: '8px' }}>Gestion des remboursements</li>
                                            <li style={{ marginBottom: '8px' }}>Protection contre la fraude</li>
                                            <li>Rapports détaillés des revenus</li>
                                        </ul>
                                    </div>

                                    <p style={{ marginBottom: '8px' }}>
                                        <strong>Besoin d&apos;aide ?</strong>
                                    </p>
                                    <p>
                                        Consultez la{' '}
                                        <a 
                                            href="https://stripe.com/docs" 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            style={{ color: '#ff6a3d', fontWeight: 600, textDecoration: 'underline' }}
                                        >
                                            documentation Stripe
                                        </a>
                                        {' '}ou contactez notre support pour plus d&apos;informations.
                                    </p>
                                </div>
                            </div> */}
                        </div>
                    )}

                    {/* CONTENU : Onglet Support Client */}
                    {activeTab === 'support' && (
                        <div className="tab-pane active" id="support">
                            {/* Cards Contact */}
                            <div className="support-cards-grid">
                                <div className="support-card active-card">
                                    <div className="support-icon-wrapper">
                                        <Mail size={24} />
                                    </div>
                                    <div className="support-card-content">
                                        <h4>Email</h4>
                                        <p>support@qrnyama.com</p>
                                        <div className="support-card-subtext">
                                            <Clock size={12} />
                                            <span>Réponse sous 24h</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="support-card">
                                    <div className="support-icon-wrapper">
                                        <MessageCircle size={24} />
                                    </div>
                                    <div className="support-card-content">
                                        <h4>Chat en direct</h4>
                                        <p>Discuter avec un conseiller</p>
                                        <div className="support-card-subtext online">
                                            <CheckCircle size={12} />
                                            <span>En ligne</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="support-card">
                                    <div className="support-icon-wrapper">
                                        <Phone size={24} />
                                    </div>
                                    <div className="support-card-content">
                                        <h4>Téléphone</h4>
                                        <p>+33 1 23 45 67 89</p>
                                        <div className="support-card-subtext">
                                            <Clock size={12} />
                                            <span>Lun-Ven 9h-18h</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* FAQ Section */}
                            <div className="settings-card">
                                <div className="card-title">
                                    <BookOpen size={18} />
                                    <span>Questions fréquentes</span>
                                </div>
                                <div className="faq-list">
                                    {faqs.map((faq, index) => (
                                        <div className="faq-item" key={index}>
                                            <button 
                                                className="faq-question" 
                                                onClick={() => toggleFaq(index)}
                                                aria-expanded={faqOpenIndex === index}
                                                type={"button"}
                                            >
                                                <span>{faq.question}</span>
                                                {faqOpenIndex === index ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                            </button>
                                            {faqOpenIndex === index && (
                                                <div className="faq-answer">
                                                    {faq.answer}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Submit Ticket Form */}
                            <div className="settings-card">
                                <div className="card-title">
                                    <Headphones size={18} />
                                    <span>Envoyer un ticket</span>
                                </div>
                                <form onSubmit={handleTicketSubmit}>
                                    <div className="billing-form" style={{ marginBottom: '20px' }}>
                                        <div className="form-group">
                                            <label>Sujet</label>
                                            <input 
                                                type="text" 
                                                className="form-input" 
                                                placeholder="Décrivez brièvement votre problème"
                                                value={ticketData.sujet}
                                                onChange={(e) => setTicketData({...ticketData, sujet: e.target.value})}
                                                required
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Catégorie</label>
                                            <select 
                                                className="form-input"
                                                value={ticketData.categorie}
                                                onChange={(e) => setTicketData({...ticketData, categorie: e.target.value})}
                                                required
                                            >
                                                <option value="" disabled>Sélectionner</option>
                                                <option value="technique">Problème technique</option>
                                                <option value="facturation">Facturation</option>
                                                <option value="autre">Autre demande</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="form-group full-width" style={{ marginBottom: '20px' }}>
                                        <label>Message</label>
                                        <textarea 
                                            className="form-input" 
                                            placeholder="Décrivez votre problème en détail..."
                                            rows="4"
                                            value={ticketData.message}
                                            onChange={(e) => setTicketData({...ticketData, message: e.target.value})}
                                            required
                                            style={{ resize: 'vertical' }}
                                        ></textarea>
                                    </div>
                                    <button type="submit" className="btn-primary-settings" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <Send size={16} />
                                        Envoyer le ticket
                                    </button>
                                </form>
                            </div>

                            {/* Resources Footer */}
                            <div className="settings-card" style={{ padding: '20px 24px' }}>
                                <div className="card-title" style={{ marginBottom: '16px', fontSize: '14px' }}>
                                    <ExternalLink size={16} color="#6b7280" />
                                    <span style={{ color: '#374151' }}>Ressources utiles</span>
                                </div>
                                <div className="resources-list" style={{ marginTop: '0' }}>
                                    <a href="#" className="resource-pill" onClick={(e) => e.preventDefault()}>
                                        <BookOpen size={14} /> Documentation
                                    </a>
                                    <a href="#" className="resource-pill" onClick={(e) => e.preventDefault()}>
                                        <Video size={14} /> Tutoriels vidéo
                                    </a>
                                    <a href="#" className="resource-pill" onClick={(e) => e.preventDefault()}>
                                        <MessageSquare size={14} /> Communauté
                                    </a>
                                    <a href="#" className="resource-pill" onClick={(e) => e.preventDefault()}>
                                        <FileText size={14} /> Nouveautés
                                    </a>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* CONTENU : Onglet Notifications */}
                    {activeTab === 'notifications' && (
                        <div className="tab-pane active" id="notifications">
                            <div className="settings-card">
                                <div className="card-title">
                                    <Smartphone size={18} />
                                    <span>Notifications dans l&apos;application</span>
                                </div>
                                <div className="notification-group">
                                    {[
                                        { id: 'stockLow', title: 'Stock bas', desc: "Alerte lorsqu'un ingrédient atteint le seuil minimum" },
                                        { id: 'outOfStock', title: 'Rupture de stock', desc: "Alerte lorsqu'un ingrédient est épuisé" },
                                        { id: 'paymentIssue', title: 'Problème de paiement', desc: "Alerte en cas d'échec de paiement" }
                                    ].map(item => (
                                        <div className="notification-item" key={item.id}>
                                            <div className="notification-info">
                                                <h4>{item.title}</h4>
                                                <p>{item.desc}</p>
                                            </div>
                                            <label className="toggle-switch">
                                                <input 
                                                    type="checkbox" 
                                                    checked={notifSettings[item.id]} 
                                                    onChange={() => handleNotifToggle(item.id)} 
                                                />
                                                <span className="toggle-slider"></span>
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="settings-card">
                                <div className="card-title">
                                    <Mail size={18} />
                                    <span>Courriel de notification</span>
                                </div>
                                <div className="notification-group">
                                    <div className="notification-item" style={{ borderBottom: '1px solid #f3f4f6', paddingBottom: '24px' }}>
                                        <div className="notification-info">
                                            <h4>Activer les notifications email</h4>
                                            <p>Recevoir les alertes importantes par email</p>
                                        </div>
                                        <label className="toggle-switch">
                                            <input 
                                                type="checkbox" 
                                                checked={notifSettings.emailEnabled} 
                                                onChange={() => handleNotifToggle('emailEnabled')} 
                                            />
                                            <span className="toggle-slider"></span>
                                        </label>
                                    </div>
                                    
                                    <div className="frequency-group" style={{ opacity: notifSettings.emailEnabled ? 1 : 0.5, pointerEvents: notifSettings.emailEnabled ? 'auto' : 'none' }}>
                                        <label>
                                            <Clock size={16} /> Fréquence des notifications
                                        </label>
                                        <select 
                                            className="form-input" 
                                            style={{ maxWidth: '300px' }}
                                            value={notifSettings.emailFrequency}
                                            onChange={handleFrequencyChange}
                                        >
                                            <option value="realtime">Temps réel</option>
                                            <option value="daily">Quotidien</option>
                                            <option value="weekly">Hebdomadaire</option>
                                        </select>
                                        <p>Vous recevez un email pour chaque événement important.</p>
                                    </div>
                                </div>
                            </div>

                            <div className="notification-alert">
                                <Bell size={18} />
                                <span>Les notifications critiques (problèmes de paiement, alertes de sécurité) seront toujours envoyées, même si les notifications sont désactivées.</span>
                            </div>
                        </div>
                    )}

                    {/* ÉTAT VIDE : Pour les onglets non encore implémentés */}
                    {activeTab !== 'subscription' && activeTab !== 'payment' && activeTab !== 'support' && activeTab !== 'notifications' && (
                        <div className="placeholder-content" style={{ padding: '60px', textAlign: 'center', border: '1px dashed #d1d5db', borderRadius: '16px', color: '#6b7280' }}>
                            <p>Le contenu pour l&apos;onglet <strong>{tabs.find(t => t.id === activeTab).label}</strong> est actuellement en cours de développement.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    </div>
);
};

export default Settings;
